import React from 'react';
import useLanguage from '../../hooks/useLanguage';
import { useAirbnbOffers, useRefreshAirbnbOffers } from '../../hooks/usePricingGuidance';
import { formatShortDateTime } from '../../utils/dateFormatter';
import { PRICING_ERROR_CODES } from '../../utils/pricingErrorCodes';
import { accountRuleText, promotionText, ruleDetail, ruleName, ruleValue } from './airbnbOfferText';

/**
 * «الخصومات والعروض الحالية» — what this unit's Airbnb listing is running right now, so ops see it
 * before quoting or offering more (tasks/pricing-listing-build-brainstorm.md, Phase 1 slice 1a).
 *
 * READ-ONLY. Everything shown comes from the backend's saved Channex read (nightly + «تحديث»); every
 * flag (stale, recent change, promotion overriding the discounts) is decided on the server. Airbnb
 * only: Booking.com and Gathern discounts are not on Channex and are not shown — the label says so,
 * so "no discounts here" is never read as "no discounts anywhere". Editing stays on Airbnb until
 * Phase 2.
 *
 * No base price on purpose: Airbnb's default price is a fallback the calendar overrides every night,
 * and showing it beside the 30-day published prices below would read as a second price.
 */

// "TYPE:days" — the server's key for a rule, used to name one that was removed.
const nameFromKey = (key, t) => {
  const [type, days] = String(key).split(':');
  return ruleName(type, days === '' ? null : Number(days), t);
};

const RefreshOutcome = ({ outcome, t }) => {
  if (!outcome || outcome.done) return null;
  if (outcome.reason === 'cooldown') return <p className="pg-offers__note">{t('pricing.offers.refreshCooldown')}</p>;
  if (outcome.reason === 'timeout') return <p className="pg-offers__note">{t('pricing.offers.refreshTimeout')}</p>;
  if (outcome.reason === 'failed') return <p className="pg-offers__note">{t('pricing.offers.refreshFailed')}</p>;
  if (outcome.reason === 'mock' || outcome.reason === 'disabled') return <p className="pg-offers__note">{t('pricing.offers.refreshUnavailable')}</p>;
  return null;
};

const OffersBody = ({ data, t, language, isArabic }) => {
  const rules = Array.isArray(data.rules) ? data.rules : [];
  const promotions = Array.isArray(data.runningPromotions) ? data.runningPromotions : [];
  const adjustments = Array.isArray(data.account?.rateAdjustments) ? data.account.rateAdjustments : [];
  const change = data.change || null;
  const changedKeys = new Set(change?.keys || []);
  const overridden = Boolean(data.promotionCancelsDiscounts);

  return (
    <>
      {data.stale && (
        <div className="pg-notice pg-notice--warn">
          <div className="pg-notice__title">{t('pricing.offers.staleTitle')}</div>
          {t('pricing.offers.staleBody')}
        </div>
      )}

      {data.published === false && (
        <p className="pg-offers__line"><span className="pg-tag pg-tag--stop">{t('pricing.offers.unpublished')}</span></p>
      )}

      {/* Always a line, even with no rule: the +15% coming OFF is as important as it going on (it
          follows the owner's VAT number), and an empty space would carry no «تغيّر» mark. */}
      {(adjustments.length ? adjustments : [null]).map(a => (
        <p key={a ? `${a.mode}-${a.value}` : 'none'} className="pg-offers__line">
          {a ? accountRuleText(a, t) : t('pricing.offers.noAccountRule')}
          {change?.accountChanged && <span className={`pg-tag${change.recent ? ' pg-tag--quiet' : ''}`}>{t('pricing.offers.changed')}</span>}
        </p>
      ))}

      {overridden && (
        <div className="pg-notice pg-notice--warn">
          <div className="pg-notice__title">{t('pricing.offers.overrideTitle')}</div>
          {t('pricing.offers.overrideBody')}
        </div>
      )}

      <h3 className="pg-offers__label">{t('pricing.offers.promotionsLabel')}</h3>
      <p className="pg-offers__line">
        {promotions.length === 0 ? (
          <span className="pg-table__empty">{t('pricing.offers.noPromotions')}</span>
        ) : promotions.map(p => (
          <span key={p.type} className="pg-tag pg-tag--promo">
            {promotionText(p, t)}
          </span>
        ))}
        {change?.promotionsChanged && <span className={`pg-tag${change.recent ? ' pg-tag--quiet' : ''}`}>{t('pricing.offers.changed')}</span>}
      </p>

      <h3 className="pg-offers__label">{t('pricing.offers.discountsLabel')}</h3>
      {rules.length === 0 ? (
        <p className="pg-table__empty">{t('pricing.offers.noDiscounts')}</p>
      ) : (
        <table className={`pg-table pg-offers__table${overridden ? ' pg-offers__table--overridden' : ''}`}>
          <thead>
            <tr>
              <th>{t('pricing.offers.discountColumn')}</th>
              <th>{t('pricing.offers.valueColumn')}</th>
            </tr>
          </thead>
          <tbody>
            {rules.map(rule => {
              const detail = ruleDetail(rule.type, rule.days, t, isArabic);
              return (
              <tr key={rule.key}>
                <td>
                  <span className="pg-offers__name">{ruleName(rule.type, rule.days, t)}</span>
                  {detail && <span className="pg-offers__detail">{detail}</span>}
                </td>
                <td>
                  <span className="pg-offers__value">{ruleValue(rule, t)}</span>
                  {changedKeys.has(rule.key) && (
                    <span className={`pg-tag${change.recent ? ' pg-tag--quiet' : ''} pg-offers__changed`}>{t('pricing.offers.changed')}</span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {change && (
        <p className="pg-offers__change">
          {t('pricing.offers.changeNoticed')} {formatShortDateTime(change.noticedAt, language)}
          {change.previousReadAt && <> ({t('pricing.offers.changeAfter')} {formatShortDateTime(change.previousReadAt, language)})</>}
          {(change.removed || []).length > 0 && (
            <> · {t('pricing.offers.changeRemoved')}: {change.removed.map(k => nameFromKey(k, t)).join(t('pricing.common.listSeparator'))}</>
          )}
          {change.publishedChanged && <> · {t('pricing.offers.changePublished')}</>}
        </p>
      )}
    </>
  );
};

const AirbnbOffersCard = ({ propertyId }) => {
  const { t, language, isArabic } = useLanguage();
  const offers = useAirbnbOffers(propertyId);
  const refresh = useRefreshAirbnbOffers(propertyId);

  // Switched off on this server: the card simply isn't there.
  if (offers.error?.response?.data?.code === PRICING_ERROR_CODES.OFFERS_DISABLED) return null;

  const data = offers.data;
  const canRefresh = Boolean(data) && data.status !== 'no_listing';

  return (
    <section className="pg-block pg-offers">
      <div className="pg-block__head pg-offers__head">
        <div className="pg-offers__heading">
          <h2 className="pg-block__title">
            <i className="bi bi-tag" />
            {t('pricing.offers.heading')}
          </h2>
          <p className="pg-block__hint">
            {t('pricing.offers.airbnbOnly')}
            {/* The account name starts with "Airbnb", so an auto-direction isolate laid it out
                left-to-right and an Arabic reader met the owner's name before the platform's. */}
            {data?.account?.title && <> · {t('pricing.offers.account')}: <bdi dir={isArabic ? 'rtl' : 'ltr'}>{data.account.title}</bdi></>}
            {data?.lastReadAt && <> · {t('pricing.offers.lastRead')} {formatShortDateTime(data.lastReadAt, language)}</>}
          </p>
        </div>
        {canRefresh && (
          <button
            type="button"
            className="btn btn-outline-primary btn-sm pg-offers__refresh"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            <i className="bi bi-arrow-clockwise" />
            {refresh.isPending ? t('pricing.offers.refreshing') : t('pricing.offers.refresh')}
          </button>
        )}
      </div>

      {offers.isLoading && (
        <div className="text-center">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">{t('pricing.offers.loading')}</span>
          </div>
        </div>
      )}

      {offers.isError && (
        <>
          <div className="pg-notice pg-notice--blocked">{t('pricing.offers.loadError')}</div>
          <button type="button" className="btn btn-outline-primary btn-sm" style={{ marginTop: 10 }} onClick={() => offers.refetch()}>
            {t('pricing.page.retry')}
          </button>
        </>
      )}

      <RefreshOutcome outcome={refresh.data?.refresh} t={t} />
      {refresh.isError && <p className="pg-offers__note">{t('pricing.offers.refreshFailed')}</p>}

      {data?.status === 'no_listing' && <p className="pg-table__empty">{t('pricing.offers.noListing')}</p>}
      {data?.status === 'not_read_yet' && <p className="pg-table__empty">{t('pricing.offers.notReadYet')}</p>}
      {data?.status === 'not_on_account' && <div className="pg-notice pg-notice--warn">{t('pricing.offers.notOnAccount')}</div>}
      {data?.status === 'ok' && <OffersBody data={data} t={t} language={language} isArabic={isArabic} />}
    </section>
  );
};

export default AirbnbOffersCard;
